import { useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { useForm } from '@mantine/form';
import { DateTimePicker } from '@mantine/dates';
import { Modal, FocusTrap, Box, Button, Select, TextInput } from '@mantine/core';
import { EDIT_ADMIN_USER_PLAN } from '../../graphql/mutations';
import { Error } from '../Error';

type UserPlanFormValues = {
  planSlug: string,
  planEndDate: Date | string,
  billingBeginDate: Date | string,
  blockedTill: Date | string,
  telegramUsername: string | null
}

type EditUserPlanModalProps = {
  user: any | null,
  plans: string[],
  refetchQueries: any[],
  onClose: () => void
}

export function EditUserPlanModal({ user, plans, refetchQueries, onClose }: EditUserPlanModalProps) {
  const form = useForm<UserPlanFormValues>({
    initialValues: {
      planSlug: "",
      planEndDate: "",
      billingBeginDate: "",
      blockedTill: "",
      telegramUsername: ""
    },
  });

  const [editPlan, editPlanState] = useMutation(EDIT_ADMIN_USER_PLAN, {
    refetchQueries,
    onCompleted() {
      form.reset();
      onClose();
    }
  });

  useEffect(() => {
    if (!user) {
      return;
    }
    form.setValues({
      planSlug: user.data?.planSlug || "",
      planEndDate: user.data?.planEndDate ? new Date(user.data.planEndDate) : "",
      billingBeginDate: user.data?.billingBeginDate ? new Date(user.data.billingBeginDate) : "",
      blockedTill: user.data?.blockedTill ? new Date(user.data.blockedTill) : "",
      telegramUsername: user.data?.telegramUsername || ""
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const save = () => {
    if (!user) {
      return;
    }
    editPlan({
      variables: {
        userId: user.id,
        planSlug: form.values.planSlug,
        planEndDate: form.values.planEndDate || null,
        billingBeginDate: form.values.billingBeginDate || null,
        blockedTill: form.values.blockedTill || null,
        telegramUsername: form.values.telegramUsername || null
      }
    });
  };

  return (
    <Modal opened={!!user} title="Edit user plan" withCloseButton onClose={onClose} size="lg" radius="md">
      <Box mih={400}>
        <FocusTrap active={!!user}>
          <Error text={editPlanState?.error?.toString()} />
          <DateTimePicker
            label="Plan end date"
            valueFormat="DD MMM YYYY hh:mm A"
            placeholder="Pick date and time"
            {...form.getInputProps('planEndDate')}
            clearable
          />
          <DateTimePicker
            label="Billing begin date"
            valueFormat="DD MMM YYYY hh:mm A"
            placeholder="Pick date and time"
            {...form.getInputProps('billingBeginDate')}
          />
          <DateTimePicker
            label="Blocked till date"
            valueFormat="DD MMM YYYY hh:mm A"
            placeholder="Pick date and time"
            {...form.getInputProps('blockedTill')}
            clearable
          />
          <TextInput label="Telegram username" placeholder="Telegram username" {...form.getInputProps('telegramUsername')} />
          <Select label="Plan" placeholder="" data={plans} {...form.getInputProps('planSlug')} />
          <Button color={"green"} loading={editPlanState.loading} onClick={save}>Save</Button>
        </FocusTrap>
      </Box>
    </Modal>
  );
}
